// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * I-9 Compliance Documentation Dialog
 *
 * Explains the project rationale, cost analysis, and technical approach.
 */
import type { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Button,
  ScrollArea,
} from '@/components/ui'
import { BookOpen, DollarSign, Workflow, Database, FileSearch, CheckCircle2 } from 'lucide-react'

export const DocsDialog: FC = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1">
          <BookOpen className="h-4 w-4" />
          Docs
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <BookOpen className="h-5 w-5" />
            I-9 Compliance Verification
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[65vh] pr-4">
          <div className="space-y-8 text-sm">
            {/* Problem Statement */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <FileSearch className="h-5 w-5 text-blue-500" />
                The Problem
              </h2>
              <p className="text-muted-foreground mb-3">
                UMD has approximately <strong>1 million paper I-9 forms</strong> scanned into Optix.
                Each record contains 4-5 documents (I-9 form, driver's license, passport, etc.).
                Federal law requires I-9 retention for all U.S. employees, but most of these forms
                can be legally purged.
              </p>
              <p className="text-muted-foreground">
                Processing all forms with commercial OCR costs $70,000+ just for text extraction—before
                any verification. We need a smarter approach.
              </p>
            </section>

            {/* Cost Analysis */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-500" />
                Cost Analysis
              </h2>
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Brute Force Approach</p>
                    <p className="text-2xl font-bold text-destructive">$70,000+</p>
                    <p className="text-xs text-muted-foreground">Process all 1M forms with commercial OCR</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Smart Filtering</p>
                    <p className="text-2xl font-bold text-green-600">~Free</p>
                    <p className="text-xs text-muted-foreground">Pre-filter to ~30,000 relevant forms</p>
                  </div>
                </div>
                <div className="pt-3 border-t text-xs text-muted-foreground">
                  <strong>Why free?</strong> DIT is purchasing a GPU for on-premise AI workloads.
                  Vision extraction runs locally—no per-document cloud fees. HR pays nothing for the filtering step.
                </div>
              </div>
            </section>

            {/* How It Works */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Workflow className="h-5 w-5 text-purple-500" />
                How It Works
              </h2>
              <div className="space-y-4">
                <Step
                  number={1}
                  title="Identify I-9 Form"
                  description="Use free Tesseract OCR to find the main I-9 form in each multi-page PDF. Filters out licenses, passports, and other attached documents."
                  cost="Free"
                />
                <Step
                  number={2}
                  title="Extract Identity Fields"
                  description="Run the I-9 page through Qwen vision model to extract handwritten fields: SSN, First Name, Last Name, Date of Birth."
                  cost="~$0.01/form"
                />
                <Step
                  number={3}
                  title="Match Against HR Records"
                  description="Query Oracle (UPS_PERS) by SSN to find the employee. Verify name and DOB match. Get hire date and separation date from PHR tables."
                  cost="Free"
                />
                <Step
                  number={4}
                  title="Calculate Retention"
                  description="Apply federal I-9 retention rule: MAX(hire + 3 years, separation + 1 year). Forms past retention can be purged."
                  cost="Free"
                />
              </div>
            </section>

            {/* Retention Rules */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Database className="h-5 w-5 text-amber-500" />
                Federal I-9 Retention Rules
              </h2>
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <p className="font-mono text-sm bg-background p-2 rounded border">
                  Retention Period = MAX(hire_date + 3 years, separation_date + 1 year)
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium">Current Employees</p>
                    <p className="text-muted-foreground text-xs">Must retain—no separation date</p>
                  </div>
                  <div>
                    <p className="font-medium">Former Employees</p>
                    <p className="text-muted-foreground text-xs">Retain until retention period expires</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground pt-2 border-t">
                  Example: Employee hired Jan 2020, separated Jun 2022.
                  Retention expires Jun 2023 (separation + 1 year). Eligible for purge after that date.
                </p>
              </div>
            </section>

            {/* Output Categories */}
            <section>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Output Categories
              </h2>
              <div className="space-y-3">
                <OutputCategory
                  name="Matched"
                  color="green"
                  description="SSN, name, and DOB all match. Employee found in HR system. Retention calculated automatically."
                />
                <OutputCategory
                  name="Partial Match"
                  color="amber"
                  description="SSN matches but name or DOB differs. May be OCR error, name change, or typo. Requires manual review."
                />
                <OutputCategory
                  name="No Match"
                  color="red"
                  description="No employee found with that SSN. Could be contractor, terminated long ago, or OCR extraction error."
                />
                <OutputCategory
                  name="Purgeable"
                  color="blue"
                  description="Matched employee whose retention period has expired. Safe to purge per federal guidelines."
                />
              </div>
            </section>

            {/* Data Sources */}
            <section>
              <h2 className="text-lg font-semibold mb-3">Data Sources</h2>
              <div className="text-sm space-y-2 text-muted-foreground">
                <p><strong>Optix RDS (Oracle ITX)</strong> — Document metadata, scan dates</p>
                <p><strong>UPS_PERS (Oracle ITA)</strong> — Employee identity: SSN, name, DOB, UID</p>
                <p><strong>PHR Tables (Oracle ITW)</strong> — Employment dates: hire, separation</p>
              </div>
            </section>

            {/* Debug Mode */}
            <section className="pb-4">
              <h2 className="text-lg font-semibold mb-3">Debug Mode</h2>
              <p className="text-sm text-muted-foreground">
                The Debug button allows testing with override values. Enter test identity fields to
                verify matching logic without relying on OCR extraction. Enable date overrides to
                test retention calculations with simulated hire/separation dates.
              </p>
            </section>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}

// Helper components

interface StepProps {
  number: number
  title: string
  description: string
  cost: string
}

const Step: FC<StepProps> = ({ number, title, description, cost }) => (
  <div className="flex gap-3">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
      {number}
    </div>
    <div className="flex-1">
      <div className="flex items-center justify-between">
        <p className="font-medium">{title}</p>
        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">{cost}</span>
      </div>
      <p className="text-muted-foreground text-xs mt-1">{description}</p>
    </div>
  </div>
)

interface OutputCategoryProps {
  name: string
  color: 'green' | 'amber' | 'red' | 'blue'
  description: string
}

const OutputCategory: FC<OutputCategoryProps> = ({ name, color, description }) => {
  // Use solid backgrounds with white text for proper contrast (WCAG AA)
  const colorClasses = {
    green: 'bg-green-600 text-white dark:bg-green-600',
    amber: 'bg-amber-500 text-white dark:bg-amber-500',
    red: 'bg-red-600 text-white dark:bg-red-600',
    blue: 'bg-blue-600 text-white dark:bg-blue-600',
  }

  return (
    <div className="flex gap-3 items-start">
      <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${colorClasses[color]}`}>
        {name}
      </span>
      <p className="text-foreground text-xs flex-1">{description}</p>
    </div>
  )
}
